import React, { useState, useEffect, lazy, Suspense, createRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Row } from 'reactstrap';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { fetchMixedBundles } from 'Redux/ducks/algolia-calls';
import useViewport from 'Hooks/useViewport';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import Breadcrumbs from 'Components/Utility/SEO/Breadcrumbs';
import BundleDeadLink from 'Components/Bundle/BundleDeadlink';
import BundleEndedBanner from 'Components/Bundle/BundleEnded/BundleEndedBanner';
import BundlePillFilters from 'Components/Bundle/BundlePillFilters';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import HitCard from 'Components/Cards/HitCard';
import messages from 'Containers/Bundle/BundleMessages';
import Meta from 'Components/Utility/SEO/Meta';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import TakeOver from 'Components/Banner/TakeOver';
import ProductCarouselSection from 'Containers/HomePage/ProductCarouselSection';
import sortByMessages from 'Components/Utility/SortBy/SortByMessages';
import SortByDropdown from 'Components/Utility/SortBy/SortByDropdown';

import 'Components/StaticSearch/StaticFilters.scss';
import 'Containers/Bundle/BundlesLandingPage.scss';

const BundleFestCountdown = lazy(() => import(/* webpackChunkName: "BundleFestCountdown" */ 'Components/Banner/BundleFestCountdown/BundleFestCountdown'));

const BundlesContainer = ({
  intl: { formatMessage },
  match,
  liveDisplayTypes,
}) => {
  const viewLatestDeals = getUrlParameter('view') === 'latest-deals';
  const viewEndingSoon = getUrlParameter('view') === 'ending-soon';
  const liveDisplayTypesArray = Object.keys(liveDisplayTypes);
  const liveEbooks = liveDisplayTypesArray.includes('book-bundle') || liveDisplayTypesArray.includes('comic-bundle');
  const liveSoftware = liveDisplayTypesArray.includes('software-bundle') || liveDisplayTypesArray.includes('elearning-bundle') || liveDisplayTypesArray.includes('elearning') || liveDisplayTypesArray.includes('audio-bundle') || liveDisplayTypesArray.includes('video-bundle');

  let defaultIndex = '';

  if (viewLatestDeals) {
    defaultIndex = 'latest_deals';
  } else if (viewEndingSoon) {
    defaultIndex = 'ending_soon';
  }

  let bundleType = 'allbundles';

  if (match.path.split('/')[2]) bundleType = match.path.split('/')[2];

  const [cleanHits, setCleanHits] = useState([]);
  const [sort, setSort] = useState(defaultIndex);
  const [collapseDescription, setCollapseDescription] = useState(true);

  const descriptionRef = createRef();

  const { isBelowLg } = useViewport();

  useEffect(() => {
    const loadBundles = async () => {
      setCleanHits([]);
      const hits = await fetchMixedBundles(sort);
      if (Array.isArray(hits)) setCleanHits(hits);
    };

    loadBundles();
  }, [sort]);

  let filteredHits = [];
  if (bundleType === 'allbundles') filteredHits = cleanHits;
  if (bundleType === 'games') filteredHits = filteredHits.concat(cleanHits.filter(hit => hit.display_type === 'bundle'));
  if (bundleType === 'books') filteredHits = filteredHits.concat(cleanHits.filter(hit => hit.display_type === 'book-bundle' || hit.display_type === 'comic-bundle'));
  if (bundleType === 'software') filteredHits = filteredHits.concat(cleanHits.filter(hit => hit.display_type === 'software-bundle' || hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning' || hit.display_type === 'audio-bundle' || hit.display_type === 'video-bundle'));

  const gameHits = cleanHits.filter(hit => hit.display_type === 'bundle');
  const bookHits = cleanHits.filter(hit => hit.display_type === 'book-bundle' || hit.display_type === 'comic-bundle');
  const softwareHits = cleanHits.filter(hit => hit.display_type === 'software-bundle' || hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning' || hit.display_type === 'audio-bundle' || hit.display_type === 'video-bundle');

  const bundleEndedName = window.location.hash.startsWith('#bundle-ended-') ? decodeURIComponent(window.location.hash.replace('#bundle-ended-', '')) : null;

  let deadLink = false; // when a user access bundle type with no live products
  if (bundleType === 'games' && !liveDisplayTypesArray.includes('bundle')) deadLink = true;
  if (bundleType === 'books' && !liveEbooks) deadLink = true;
  if (bundleType === 'software' && !liveSoftware) deadLink = true;

  const crumbs = [
    { label: formatMessage(messages.title), link: '/bundle' },
  ];

  if (bundleType !== 'allbundles') {
    crumbs.push({
      label: formatMessage(messages[`${bundleType}_title`]),
      link: `/bundle/${bundleType}`,
    });
  }

  let showCondensedLayout = false;
  if (bundleType === 'allbundles') showCondensedLayout = true;

  const onSortByChange = (sortValue) => {
    setSort(sortValue);
  };

  const sortByOptions = [
    { name: formatMessage(sortByMessages.topSellers), value: '' },
    { name: formatMessage(sortByMessages.priceLowHigh), value: 'price_asc' },
    { name: formatMessage(sortByMessages.priceHighLow), value: 'price_desc' },
    { name: formatMessage(sortByMessages.alphabetical), value: 'name' },
    { name: formatMessage(sortByMessages.topDiscount), value: 'discount' },
    { name: formatMessage(sortByMessages.latestDeals), value: 'latest_deals' },
    { name: formatMessage(sortByMessages.endingSoon), value: 'ending_soon' },
    { name: formatMessage(sortByMessages.mostWanted), value: 'most_wanted' },
    { name: formatMessage(sortByMessages.releaseDate), value: 'release_date_desc' },
    { name: formatMessage(sortByMessages.topRated), value: 'top_rated' },
  ];

  const maxHeight = 40;
  const expandDescription = () => {
    descriptionRef.current.style.maxHeight = '100%';
    setCollapseDescription(!collapseDescription);
  };

  return (
    <div className="bundle-landing-page">
      {bundleType === 'allbundles' ?
        <Meta
          brandReverse
          title={formatMessage(messages.title)}
          description={formatMessage(messages.desc || messages.fullDesc)}
          socialTitle={formatMessage(messages.socialTitle)}
          url="bundle"
          hits={cleanHits}
          pageType="CollectionPage"
        />
        :
        <Meta
          brandTitle
          title={formatMessage(messages[`${bundleType}_title`])}
          description={formatMessage(messages[`${bundleType}_desc`] || messages[`${bundleType}_fullDesc`])}
          socialTitle={formatMessage(messages[`${bundleType}_socialTitle`])}
          url={`bundle/${bundleType}`}
          hits={cleanHits}
          pageType="CollectionPage"
        />
      }

      <Breadcrumbs crumbs={crumbs} />

      <TakeOver appearsOn="bundles" />

      {deadLink &&
        <BundleDeadLink bundles={cleanHits.slice(0, 16)} bundleType={bundleType} />
      }

      {!deadLink &&
        <React.Fragment>
          <div className="pt-5 container">
            {!!bundleEndedName &&
              <BundleEndedBanner name={bundleEndedName} />
            }

            <Suspense>
              <BundleFestCountdown bundlesPage />
            </Suspense>

            <div className="title-container">
              <InView>
                {({ inView, ref }) =>
                  <div ref={ref}>
                    <h1>
                      {formatMessage(messages[`${bundleType}_title`] || messages.title)}
                    </h1>
                    {!inView &&
                      <div className="d-block d-sm-none">
                        <Backtotopbutton />
                      </div>
                    }
                  </div>
                }
              </InView>
            </div>

            {isBelowLg &&
              <>
                <div className="SearchPage__description">
                  <div className="SearchPage__description__content" style={{ maxHeight }} ref={descriptionRef}>
                    <p>
                      {formatMessage(messages[`${bundleType}_fullDesc`] || messages.fullDesc)}
                    </p>
                    {collapseDescription &&
                      <div className="SearchPage__description__content__gradient" />
                    }
                  </div>

                  {collapseDescription &&
                    <button className="SearchPage__description__button" onClick={() => { expandDescription(); }}>
                      <FormattedMessage id="bundles.description.show-more" defaultMessage="Show more" />
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  }
                </div>

                <div className="categories-and-sortby-section mobile-friendly">
                  <BundlePillFilters mobilePills currentBundleType={bundleType} />

                  <SortByDropdown
                    options={sortByOptions}
                    value={sort}
                    onChange={onSortByChange}
                  />
                </div>
              </>
            }

            {!isBelowLg &&
              <>
                <p className="seo-blurb-text mb-4">
                  {formatMessage(messages[`${bundleType}_fullDesc`] || messages.fullDesc)}
                </p>

                <nav data-nosnippet className={'categories-and-sortby-section d-flex'}>
                  <BundlePillFilters currentBundleType={bundleType} />

                  <div className="filters-container">
                    <div className="mr-3"><FormattedMessage id="bundles.sortby" defaultMessage="Sort by" />:</div>
                    <div className="dropdown-container sort-by">
                      <select value={sort} onChange={e => setSort(e.target.value)}>
                        <option value="">{formatMessage(sortByMessages.topSellers)}</option>
                        <option value={'price_asc'}>{formatMessage(sortByMessages.priceLowHigh)}</option>
                        <option value={'price_desc'}>{formatMessage(sortByMessages.priceHighLow)}</option>
                        <option value={'name'}>{formatMessage(sortByMessages.alphabetical)}</option>
                        <option value={'discount'}>{formatMessage(sortByMessages.topDiscount)}</option>
                        <option value={'latest_deals'}>{formatMessage(sortByMessages.latestDeals)}</option>
                        <option value={'ending_soon'}>{formatMessage(sortByMessages.endingSoon)}</option>
                        <option value={'most_wanted'}>{formatMessage(sortByMessages.mostWanted)}</option>
                        <option value={'release_date_desc'}>{formatMessage(sortByMessages.releaseDate)}</option>
                      </select>
                    </div>
                  </div>
                </nav>
              </>
            }
          </div>

          <div className="product-grid container">
            {isEmpty(filteredHits) &&
              <Row>
                <SkeletonHitCard
                  numberOfCards={24}
                  xsSize={6}
                  mdSize={6}
                  lgSize={4}
                />
              </Row>
            }

            {showCondensedLayout &&
              <>
                <ul className="HitCardsRow HitCardsRow--marginReset row">
                  {gameHits.slice(0, 3).map((hit, i) =>
                    <HitCard
                      lazy={i >= 6}
                      key={Math.random()}
                      hit={hit}
                      xsSize={6}
                      mdSize={6}
                      lgSize={4}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                    />,
                  )}
                  {gameHits.slice(3).map((hit, i) =>
                    <HitCard
                      lazy={i >= 4}
                      key={Math.random()}
                      hit={hit}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                    />,
                  )}
                </ul>

                <div className="row">
                  <ProductCarouselSection
                    title={formatMessage(messages.books_title)}
                    viewAllLocation="bundle/books"
                    loadedHits={bookHits}
                    viewAllTitle={formatMessage(messages.books_title)}
                    hitCardNameTag="h3"
                  />
                </div>

                <div className="row">
                  <ProductCarouselSection
                    title={formatMessage(messages.software_title)}
                    viewAllLocation="bundle/software"
                    loadedHits={softwareHits}
                    viewAllTitle={formatMessage(messages.software_title)}
                    hitCardNameTag="h3"
                  />
                </div>
              </>
            }

            {!showCondensedLayout && Array.isArray(filteredHits) && !!filteredHits.length &&
              <ul className="HitCardsRow row">
                {filteredHits.map((hit, i) =>
                  <HitCard
                    lazy={i >= 6}
                    key={Math.random()}
                    hit={hit}
                    xsSize={6}
                    mdSize={6}
                    lgSize={4}
                    hitCardContainerTag="li"
                    hitCardNameTag="h2"
                  />,
                )}
              </ul>
            }
          </div>
        </React.Fragment>
      }
    </div>
  );
};

BundlesContainer.propTypes = {
  liveDisplayTypes: PropTypes.shape({}),
  match: PropTypes.shape({
    params: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

BundlesContainer.defaultProps = {
  liveDisplayTypes: {},
  match: {
    params: {
      type: '',
    },
  },
};

const mapStateToProps = ({
  liveDisplayTypes,
  siteTheme,
}) => ({
  liveDisplayTypes,
  siteTheme,
});

export default connect(mapStateToProps, null)(injectIntl(BundlesContainer));
